'use client'

import { cn } from '@cocoonspace/ui/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { Tabs as TabsPrimitive } from 'radix-ui'
import * as React from 'react'

const Tabs = TabsPrimitive.Root

const tabsListVariants = cva('inline-flex items-center justify-center', {
	defaultVariants: {
		variant: 'default',
	},
	variants: {
		variant: {
			default: 'gap-2 rounded bg-white p-2 text-muted-foreground shadow-sm',
			material: 'pt-[4px]',
		},
	},
})

const TabsList = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.List>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> &
		VariantProps<typeof tabsListVariants>
>(({ variant, className, ...props }, ref) => (
	<TabsPrimitive.List
		className={tabsListVariants({ className, variant })}
		ref={ref}
		{...props}
	/>
))

TabsList.displayName = TabsPrimitive.List.displayName

const tabsTriggerVariants = cva(
	[
		'inline-flex items-center justify-center whitespace-nowrap px-3 py-1 font-medium text-foreground text-sm ring-offset-background transition-all',
		'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
	],
	{
		defaultVariants: {
			variant: 'button',
		},
		variants: {
			variant: {
				button:
					'rounded-sm hover:bg-slate-100 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow',
				material:
					'border-transparent border-b-4 py-2 data-[state=active]:border-primary data-[state=active]:font-bold data-[state=active]:text-primary',
			},
		},
	},
)

const TabsTrigger = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Trigger>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> &
		VariantProps<typeof tabsTriggerVariants>
>(({ variant, className, ...props }, ref) => (
	<TabsPrimitive.Trigger
		className={tabsTriggerVariants({ className, variant })}
		ref={ref}
		{...props}
	/>
))

TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
	<TabsPrimitive.Content
		className={cn(
			'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
			className,
		)}
		ref={ref}
		{...props}
	/>
))

TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsContent, TabsList, TabsTrigger }
